import DatasetSelector from "@/components/EcoInventDatasetSelector/DatasetSelector";
import Message from "@/components/Message/Message";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import Skeleton from "@/components/Skeleton/Skeleton";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import useAnalytics from "@/hooks/useAnalytics";
import useTranslation from "@/hooks/useTranslation";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, InputNumber, message, Space, Tabs } from "antd";
import { useWatch } from "antd/es/form/Form";
import { useEffect } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import DatasetDetails from "../../DatasetDetails";
import styles from "./../../ImpactReferenceDetails.module.scss";
import DeleteCustomImpactReferenceAction from "./DeleteCustomImpactReferenceAction";
import {
  CustomImpactReferenceExchange,
  PostCustomImpactReference,
  useGetCustomImpactReference,
  useInsertCustomImpactReference,
  useUpdateCustomImpactReference,
} from "./useCustomImpactReference";

export interface CustomImpactReferenceForm {
  elementary_eco_invent_dataset_id: number | null;
  name: string;
  geography: string;
  unit: string;
  note: string;
  exchanges: CustomImpactReferenceExchange[];
}

export default function CustomImpactReference() {
  const id = Number(useParams().referenceId);
  const addReferenceId = Number(useParams().addReferenceId);
  const [form] = Form.useForm<CustomImpactReferenceForm>();
  const analytics = useAnalytics();
  const exchanges = useWatch("exchanges", form);
  const insert = useInsertCustomImpactReference();
  const update = useUpdateCustomImpactReference();
  const location = useLocation();

  const { isLoading, data } = useGetCustomImpactReference({
    id: id || addReferenceId,
    fromEcoInventDataset: Boolean(addReferenceId),
  });

  const navigate = useNavigate();
  const t = useTranslation();

  const referenceName = !addReferenceId
    ? data?.dataset.name || ""
    : t.impactReferences.custom.addTitle;

  analytics.track("Custom Impact Reference Viewed");

  useEffect(() => {
    form.setFieldsValue({
      ...data?.dataset,
      exchanges: data?.exchanges,
    });
  }, [data]);

  const onSubmit = () => {
    form
      .validateFields()
      .then((data) => {
        const post: PostCustomImpactReference = {
          dataset: {
            note: data.note || "",
            name: data.name,
            geography: data.geography || "",
            unit: data.unit,
            elementary_eco_invent_dataset_id: data.elementary_eco_invent_dataset_id,
          },
          exchanges: data.exchanges,
        };

        if (id) {
          update
            .mutateAsync({ id, post })
            .then(() => {
              analytics.track("Custom Impact Reference Updated");
              message.success(t.impactReferences.custom.updatedSuccess);
              if (location.key !== "default") {
                navigate(-1);
              } else {
                navigate(`/impact_references/custom`);
              }
            })
            .catch((error) => {
              message.error(error.response.data.message);
            });
        } else {
          insert
            .mutateAsync({ post })
            .then(() => {
              analytics.track("Custom Impact Reference Created");
              message.success(t.impactReferences.custom.createdSuccess);
              navigate(`/impact_references/custom`);
            })
            .catch((error) => {
              message.error(t.errors.general);
            });
        }
      })
      .catch(() => {
        message.error(t.impactReferences.custom.formValidationFailedError);
      });
  };

  const cancelSaveButtons = (
    <>
      <Link to="/impact_references/custom">
        <Button>{t.general.cancel}</Button>
      </Link>
      <Button
        type="primary"
        onClick={onSubmit}
        disabled={isLoading || insert.isLoading || update.isLoading}
        loading={insert.isLoading || update.isLoading}
      >
        {t.general.save}
      </Button>
    </>
  );

  return (
    <div className={styles.ImpactReferenceDetails}>
      <PageHeader
        loading={isLoading}
        title={referenceName}
        right={cancelSaveButtons}
      />
      <Form
        form={form}
        autoComplete={"off"}
      >
        <div className={styles.form}>
          <div className={styles.left}>
            <DatasetDetails
              isCustom={true}
              isLoading={isLoading}
              title={t.impactReferences.custom.customImpactReference}
              description={t.impactReferences.custom.description}
            />

            <div className={styles.buttons}>
              <DeleteCustomImpactReferenceAction
                id={id}
                onSuccess={() => navigate("/impact_references/custom")}
              >
                <Button
                  type={"link"}
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!id || isLoading}
                >
                  {t.general.delete}
                </Button>
              </DeleteCustomImpactReferenceAction>
              <span>
                <Space>{cancelSaveButtons}</Space>
              </span>
            </div>
          </div>
          <div className={styles.right}>
            <Form.Item
              label={"Elementary exchange basis"}
              hidden={true}
              name={"elementary_eco_invent_dataset_id"}
            />
            <h2>{t.impactReferences.custom.exchanges}</h2>
            <Tabs
              items={[
                {
                  label: t.impactReferences.custom.intermediateExchanges,
                  key: "intermediate",
                  children: (
                    <div className={styles["exchanges-list"]}>
                      <Form.List name="exchanges">
                        {(fields, { add, remove }) => (
                          <>
                            <div>
                              <DatasetSelector
                                onSelect={(dataset) => {
                                  add({
                                    id: dataset.id,
                                    dataset: {
                                      id: dataset.id,
                                      name: dataset.name,
                                      geography: dataset.geography,
                                      unit: dataset.unit,
                                    },
                                    amount: undefined,
                                    not_absolute: false,
                                  });
                                  message.success(t.impactReferences.custom.exchangeAdded);
                                }}
                              >
                                <Button icon={<PlusOutlined />}>Add</Button>
                              </DatasetSelector>
                            </div>
                            <div className={styles.exchanges}>
                              {!exchanges && (
                                <div className={styles.exchange}>
                                  <Skeleton style={{ width: 200, height: 30 }} />
                                </div>
                              )}
                              {exchanges && exchanges.length === 0 && (
                                <div className={styles.exchange}>No intermediate exchanges.</div>
                              )}
                              {fields.map((field, index) => (
                                <Form.Item
                                  required={false}
                                  key={field.key}
                                  noStyle
                                >
                                  <div className={styles.exchange}>
                                    <div className={styles.data}>
                                      <Form.Item
                                        name={[field.name, "dataset", "name"]}
                                        valuePropName="children"
                                        noStyle
                                      >
                                        <span
                                          className={styles.title}
                                          children={<></>}
                                        />
                                      </Form.Item>
                                      <span className={styles.details}>
                                        <Form.Item
                                          name={[field.name, "dataset", "geography"]}
                                          valuePropName="children"
                                          noStyle
                                        >
                                          <span />
                                        </Form.Item>
                                        <span>•</span>
                                        <Form.Item
                                          name={[field.name, "dataset", "unit"]}
                                          valuePropName="children"
                                          noStyle
                                        >
                                          <span />
                                        </Form.Item>
                                      </span>
                                      <Form.Item
                                        name={[field.name, "amount"]}
                                        rules={[
                                          {
                                            required: true,
                                            message: "Amount can not be empty.",
                                          },
                                        ]}
                                        noStyle
                                      >
                                        <InputNumber
                                          controls={false}
                                          style={{ width: "200px" }}
                                        />
                                      </Form.Item>
                                    </div>
                                    <div className={styles.buttons}>
                                      <Form.Item
                                        name={[field.name, "not_absolute"]}
                                        valuePropName="checked"
                                        noStyle
                                      >
                                        <SwitchWithLabel size="small">Not absolute</SwitchWithLabel>
                                      </Form.Item>
                                      <Button
                                        danger
                                        onClick={() => remove(field.name)}
                                      >
                                        <DeleteOutlined />
                                      </Button>
                                    </div>
                                  </div>
                                </Form.Item>
                              ))}
                            </div>
                          </>
                        )}
                      </Form.List>
                    </div>
                  ),
                },
                {
                  label: t.impactReferences.custom.elementaryExchanges,
                  key: "elementary",
                  children: (
                    <div className={styles["exchanges-list"]}>
                      <Message type="note">
                        Elementary exchanges are copied from the original reference this custom
                        impact reference was based on and cannot be changed.
                      </Message>
                      <div className={styles.exchanges}>
                        {!data?.elementary_exchanges && (
                          <div className={styles.exchange}>
                            <Skeleton style={{ width: 200, height: 30 }} />
                          </div>
                        )}
                        {data?.elementary_exchanges && data?.elementary_exchanges.length === 0 && (
                          <div className={styles.exchange}>No elementary exchanges.</div>
                        )}
                        {data?.elementary_exchanges.map((exchange) => (
                          <div className={styles.exchange}>
                            <div className={styles.data}>
                              <span className={styles.title}>{exchange.name}</span>
                              <span className={styles.details}>
                                <span>
                                  {exchange.amount} {exchange.unit}
                                </span>
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </Form>
    </div>
  );
}
